import AppLabels from '../../utils/appLabels';
import React, { createContext, ForwardedRef, useContext, useEffect, useRef, useState } from 'react';
import { cond, filter, get, invoke, keys, map, matches, size, cloneDeep, replace, slice } from 'lodash';
import PropTypes from 'prop-types';
import formatString from '../../utils/formatString';
import moment from 'moment/moment';
import Modal from '../../common/Modal/Modal';
import { css } from '@emotion/css';
import { SessionContext, SessionContextType } from '../LandingPage/SessioncContext';
import { useApp } from '../../context/app-context';
import getAutoSuggestKeyWords from '../../service/get-one-time-autosuggest';
import searchProducts from '../../service/search-products';
import buyItAgainService from '../../service/buy-it-again-service';

import { otaModalConfigId } from '../../utils/constants';
import { ENABLE_OTA_OFFERS, isMobileScreen } from '../../utils/utils';
import ErrorIcon from '../../common/Icons/ErrorIcon';

const SEARCH_AUTO_POPULATE_CHAR_COUNT = 2;
const SNS_PROGRAM_TYPE = '1P_SNS';

interface ProductsAndOffers {
  source: string;
  products: any[];
  offers: any[];
}

enum ComponentType {
  PRODUCT_GRID = 'productGrid',
  OFFER_DETAIL = 'singleCouponDetail',
  MULTIPLE_COUPONS = 'multipleCoupons',
}

enum SourceResponse {
  buyItAgain = 'buyItAgain',
  search = 'search',
}

type SearchContextType = {
  searchToken: string;
  setSearchToken: (token: string) => void;
  searchState: string;
  setSearchState: (state: string) => void;
  productsAndOffers: ProductsAndOffers;
  setProductsAndOffers: (productsAndOffers: ProductsAndOffers) => void;
  selectedIndex: number;
  suggestions: any[];
  errorMessage: string;
  clearResults: () => void;
  clearSuggestions: () => void;
  handleSearch: (term: string) => void;
  handleSelect: (item: string) => void;
  numFound: number;
  nextPageToken: string;
  fulfillmentPreference: string;
  cameFromSearch: boolean;
  setCameFromSearch: (cameFromSearch: boolean) => void;
};

const addOneTimeSectionStyleExpanded = css`
  margin-bottom: 0.5rem;
  padding: 1rem 0.5rem;
  border-top: 1px solid #f1f0ee;
  border-bottom: 1px solid #f1f0ee;
  @media (max-width: 430px) {
    padding: 1rem 0;
  }
`;

const addOneTimeSectionStyleCollapsed = css`
  margin-bottom: 0;
  padding: 1rem 0;
`;

const addOneTime = css`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin: 0 0.75rem;
  flex: 1;

  @media (max-width: 430px) {
    flex-direction: column;
  }
`;

const addOneTimeDetailsStyle = css`
  font-weight: 400;
  font-size: 0.75rem;
  line-height: 1.125rem;
  padding: 0 5px;
  @media (max-width: 430px) {
    text-align: center;
    margin-bottom: 0.5rem;
  }
`;

const addOneTimeItemButtonStyle = css`
  cursor: pointer;
  flex: none;
  font-size: 14px;
  line-height: 22px;
  border-radius: 100px;
  border: 1px solid #7b1a27;
  padding: 0.5rem 1rem;
  background: #7b1a27;
  color: #ffffff;

  @media (max-width: 430px) {
    width: 100%;
  }
`;

const searchBarContainer = css`
  position: relative;
  display: flex;
  flex-direction: column;
  margin: 1rem 0.25rem 1rem 0;
  width: inherit;

  & .icon {
    padding: 0 0.25rem;
    left: 0;
    top: 0;
    bottom: 0;
    height: 100%;
    width: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  & svg {
    cursor: pointer;
  }
`;

const searchBarInnerContainer = css`
  display: flex;
  appearance: none;
  min-height: 33px;
  padding: 0.5rem 0.25rem 0.5rem 1rem;
  border: solid 2px #e5e4e3;
  background-color: #fff;
  border-radius: 100px;
  align-items: center;

  ::placeholder {
    padding-left: 2rem;
  }
`;

const focused = css`
  border: 2px solid #1f1e1e;
`;
const searchBarStyle = css`
  padding-left: 0.5rem;
  outline-style: none;
  border-radius: 100px;
  appearance: none;
  width: 100%;
  font-weight: 400;
  font-size: 1rem;
  height: 33px;
  border: none;
`;
const searchBarCaretStyle = css`
  caret-color: transparent;
`;

const closeIconStyle = css`
  padding-right: 0.75rem;
  right: 0;
  top: 0;
  bottom: 0;
  height: 100%;
  width: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const flexRow = css`
  display: flex;
  align-items: center;
`;

const oneTimeAddonsSummaryStyle = css`
  display: flex;
  padding: 1rem;
  white-space: nowrap;
`;

const submitButtonStyle = css`
  cursor: pointer;
  padding: 1rem 0.75rem;
  min-width: 12rem;
  border-radius: 6.25rem;
  background: #7b1a27;
  color: #fff;
  border: none;
  font-size: 1rem;
  line-height: 1.5rem;

  &[disabled] {
    background: #f1f0ee;
    color: #535250;
    cursor: initial;
    border: none;
  }
`;

const submitButtonContainerStyle = css`
  position: relative;
  box-shadow: 0 -4px 16px rgba(56, 51, 46, 0.16);
  margin: 0 -16px -24px;
  padding: 1rem;
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: space-between;

  @media (max-width: 641px) {
    position: fixed;
    bottom: 0;
    width: 100%;
    background: white;
    margin: 0 -16px 0;
  }
`;

const iconMarginRightSpacing = css`
  margin-right: 0.5rem;
`;

const dateSectionExpandedStyle = css`
  background-color: #faf9f8;
`;

const contentWrapperStyle = css`
  @media (min-width: 1024px) {
    width: 45rem;
    max-width: 45rem;
  }
`;

const suggestionListStyle = css`
    list-style: none;
    text-align: left;
    height: auto;
    margin: 0;
    padding: 0;

    li {
        padding: 0.5rem 0 0.5rem 20px;
        text-align: left;
        font-weight: 400;
        line-height: 20.3px;
        color: #1f1e1e;
        font-size: 1rem;

        &:hover {
            background-color: #f0f0f0;
        }

        @media (max-width: 380px) {
            padding: 5px 0 5px 6px;
        }
`;

const selectedSuggestionStyle = css`
  background-color: #f0f0f0;
`;

const suggestionListHeadingStyle = css`
  margin: 1rem 0 1rem 20px;
  text-align: left;
  font-weight: 500;
  font-size: 1.125rem;
  line-height: 1.463rem;
  @media (max-width: 430px) {
    margin-left: 6px;
    font-size: 0.875rem;
    line-height: 1.138rem;
  }
`;

const oneTimeAddModalContentStyle = css`
  height: 600px;
  overflow-x: hidden;
  overflow-y: auto;
  @media (max-width: 641px) {
    height: 80%;
  }
`;

const buyItAgainTitleStyle = css`
  font-weight: 600 !important ;
  font-size: 18px !important ;
  line-height: 23.4px !important ;
  margin-left: 10px;
`;

const productGridErrorBannerStyle = css`
  display: flex;
  align-items: baseline;
  background-color: rgba(250, 249, 248, 1);
  border: 1px solid rgba(251, 181, 181, 1);
  border-radius: 8px;
  margin-bottom: 0.875rem;
  padding: 12px 16px 12px 16px;
`;

const productGridErrorTextStyle = css`
  font-size: 12px;
  font-color: rgba(31, 30, 30, 1);
  font-weight: 400;
  padding-left: 5px;
  padding-bottom: 4px;
`;

const productGridStyle = css`
  margin-bottom: 3rem;
`;

const BackArrowIcon = (props) => {
  const handler = get(props, 'onClick');
  return (
    <div className="icon" data-testid="back-arrow">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" onClick={handler}>
        <path
          d="M8.70711 17.7929C9.09763 18.1834 9.09763 18.8166 8.70711 19.2071C8.31658 19.5976 7.68342 19.5976 7.29289 19.2071L0.792893 12.7071C0.402369 12.3166 0.402369 11.6834 0.792893 11.2929L7.29289 4.79289C7.68342 4.40237 8.31658 4.40237 8.70711 4.79289C9.09763 5.18342 9.09763 5.81658 8.70711 6.20711L3.91421 11H22C22.5523 11 23 11.4477 23 12C23 12.5523 22.5523 13 22 13H3.91421L8.70711 17.7929Z"
          fill="#1F1E1E"
        />
      </svg>
    </div>
  );
};
const SearchIcon = (props) => {
  return (
    <div className="icon">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M10.75 1C5.36522 1 1 5.36522 1 10.75C1 16.1348 5.36522 20.5 10.75 20.5C13.1724 20.5 15.3885 19.6166 17.0937 18.1543L21.7197 22.7803C22.0126 23.0732 22.4875 23.0732 22.7803 22.7803C23.0732 22.4874 23.0732 22.0125 22.7803 21.7197L18.1543 17.0937C19.6166 15.3885 20.5 13.1724 20.5 10.75C20.5 5.36522 16.1348 1 10.75 1ZM2.5 10.75C2.5 6.19365 6.19365 2.5 10.75 2.5C15.3063 2.5 19 6.19365 19 10.75C19 15.3063 15.3063 19 10.75 19C6.19365 19 2.5 15.3063 2.5 10.75Z"
          fill="#1F1E1E"
        />
      </svg>
    </div>
  );
};
const CloseIcon = (props) => {
  const handler = get(props, 'onClick');
  return (
    <div className={closeIconStyle} data-testid="close-icon">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" onClick={handler}>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M1 12C1 5.92487 5.92487 1 12 1C18.0751 1 23 5.92487 23 12C23 18.0751 18.0751 23 12 23C5.92487 23 1 18.0751 1 12ZM7.21967 16.7803C7.51256 17.0732 7.98744 17.0732 8.28033 16.7803L12 13.0607L15.7197 16.7803C16.0126 17.0732 16.4874 17.0732 16.7803 16.7803C17.0732 16.4874 17.0732 16.0126 16.7803 15.7197L13.0607 12L16.7803 8.28039C17.0732 7.9875 17.0732 7.51262 16.7803 7.21973C16.4874 6.92684 16.0125 6.92684 15.7196 7.21973L12 10.9393L8.28039 7.21973C7.9875 6.92684 7.51262 6.92684 7.21973 7.21973C6.92684 7.51262 6.92684 7.9875 7.21973 8.28039L10.9393 12L7.21967 15.7197C6.92678 16.0126 6.92678 16.4874 7.21967 16.7803Z"
          fill="#6E6E6D"
        />
      </svg>
    </div>
  );
};

const GreenRoundedIcon = () => (
  <div className={iconMarginRightSpacing}>
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0.25 11C0.25 5.06294 5.06294 0.25 11 0.25C16.9371 0.25 21.75 5.06294 21.75 11C21.75 16.9371 16.9371 21.75 11 21.75C5.06294 21.75 0.25 16.9371 0.25 11ZM16.7057 7.66852C16.9645 7.34507 16.9121 6.8731 16.5886 6.61434C16.2652 6.35559 15.7932 6.40803 15.5344 6.73147L9.46056 14.3238L6.10689 11.2496C5.80155 10.9697 5.32712 10.9904 5.04723 11.2957C4.76733 11.601 4.78796 12.0755 5.0933 12.3554L9.0383 15.9716C9.19138 16.1119 9.39578 16.1825 9.60283 16.1665C9.80987 16.1505 10.001 16.0494 10.1307 15.8873L16.7057 7.66852Z"
        fill="#04633C"
      />
    </svg>
  </div>
);

export type BackButtonContextType = {
  showBackButton: boolean;
  setShowBackButton: (state: boolean) => void;
  handleBackButtonClick: () => void;
  setBackButtonState: (state: SearchState, data?: any) => void;
  eventData: any;
  multipleCouponsData: any;
  setMultipleCouponsData: (data: any) => void;
  emitEvent: (eventData: any) => void;
  setCameFromMultipleCouponsScreen: (state: boolean) => void;
};

export const BackButtonContext = createContext<BackButtonContextType | undefined>(undefined);

interface BackButtonProviderProps {
  children: React.ReactNode;
}

export const BackButtonProvider: React.FC<BackButtonProviderProps> = ({ children }) => {
  const context = useContext(SearchContext) as SearchContextType;

  const [showBackButton, setShowBackButton] = useState(false);
  const [eventData, setEventData] = useState<any>(null);
  const [multipleCouponsData, setMultipleCouponsData] = useState<any>(null); // Store MULTIPLE_COUPONS data
  const [searchState, setSearchState] = useState<SearchState>(SearchState.MULTIPLE_COUPONS);

  const searchToken = get(context, 'searchToken');
  const handleSelect = get(context, 'handleSelect');
  const setSearchToken = get(context, 'setSearchToken');
  const cameFromSearch = get(context, 'cameFromSearch');
  const [cameFromMultipleCouponsScreen, setCameFromMultipleCouponsScreen] = useState<boolean>(false);

  // Set the back button state and store MULTIPLE_COUPONS data when transitioning
  const setBackButtonState = (state: SearchState, data?: any) => {
    setSearchState(state);
    if (state === SearchState.SINGLE_COUPON_DETAIL) {
      setShowBackButton(true);
      setEventData(data);
    } else if (state === SearchState.MULTIPLE_COUPONS) {
      setShowBackButton(true);
    } else {
      setShowBackButton(false);
    }
  };

  // Handle the back button click to navigate back to MULTIPLE_COUPONS
  const handleBackButtonClick = () => {
    if (!cameFromMultipleCouponsScreen) {
      if (cameFromSearch) {
        setShowBackButton(false);
        setSearchToken(searchToken);
        handleSelect(searchToken);
      } else {
        const loadBuyItAgainEvent = new CustomEvent('snsOneTime', {
          detail: {
            type: 'loadBuyItAgainProducts',
          },
        });
        window.dispatchEvent(loadBuyItAgainEvent);
      }
    } else {
      if (searchState === SearchState.SINGLE_COUPON_DETAIL) {
        setSearchState(SearchState.MULTIPLE_COUPONS);
        emitEvent(multipleCouponsData.multipleProduct); // Emit event with stored MULTIPLE_COUPONS data
      } else if (searchState === SearchState.MULTIPLE_COUPONS) {
        if (cameFromSearch) {
          setShowBackButton(false);
          setSearchToken(searchToken);
          handleSelect(searchToken);
        } else {
          const loadBuyItAgainEvent = new CustomEvent('snsOneTime', {
            detail: {
              type: 'loadBuyItAgainProducts',
            },
          });
          window.dispatchEvent(loadBuyItAgainEvent);
        }
      }
    }
  };

  // Function to emit events with data (e.g., for analytics or other actions)
  const emitEvent = (data: any) => {
    console.log('Event emitted with data:', data);
    const multipleCouponsEvent = new CustomEvent('snsOneTime', {
      detail: {
        type: 'multipleCoupons',
        product: { ...data },
      },
    });

    window.dispatchEvent(multipleCouponsEvent);
  };

  // Function to store the MULTIPLE_COUPONS data when switching from SINGLE_COUPON_DETAIL
  const storeMultipleCouponsData = (data: any) => {
    setMultipleCouponsData(data);
  };

  // Listen for the eventType to store MULTIPLE_COUPONS data when navigating to SINGLE_COUPON_DETAIL
  const handleEvent = (event: CustomEvent) => {
    const eventType = event.detail.type;
    if (eventType === SearchState.MULTIPLE_COUPONS) {
      // Store the MULTIPLE_COUPONS data
      const data = event.detail.data;
      storeMultipleCouponsData(data); // Store MULTIPLE_COUPONS data
    }
  };

  // Attach event listener when the provider is mounted
  React.useEffect(() => {
    window.addEventListener('snsOneTime', handleEvent as EventListener);
    return () => {
      window.removeEventListener('snsOneTime', handleEvent as EventListener);
    };
  }, []);

  return (
    <BackButtonContext.Provider
      value={{
        showBackButton,
        handleBackButtonClick,
        setBackButtonState,
        setShowBackButton,
        eventData,
        multipleCouponsData,
        setMultipleCouponsData,
        emitEvent,
        setCameFromMultipleCouponsScreen,
      }}
    >
      {children}
    </BackButtonContext.Provider>
  );
};

export const SearchContext = createContext<SearchContextType | undefined>(undefined);

interface SearchProviderProps {
  children: React.ReactNode;
  upcomingOrders: any;
}

enum SearchState {
  BUY_IT_AGAIN = 'initial',
  SEARCHING = 'searching',
  PRODUCT_RESULTS = 'results',
  NO_RESULTS = 'noResults',
  ERROR = 'error',
  SEARCH_TYPE_AHEAD_API_ERROR = 'searchTypeAheadApiError',
  BUY_IT_AGAIN_API_ERROR = 'buyItAgainApiError',
  SEARCH_PRODUCTS_API_ERROR = 'searchProductsApiError',
  TEST_OTA = 'testOta',
  SINGLE_COUPON_DETAIL = 'singleCouponDetail',
  MULTIPLE_COUPONS = 'multipleCoupons',
}

const SearchProvider = ({ children, upcomingOrders }: SearchProviderProps) => {
  const currentSessionContext = useContext(SessionContext) as SessionContextType;
  const user = get(currentSessionContext, 'user');
  const { appData, showToast } = useApp();

  const [upcomingOrder, setUpcomingOrder] = useState<any>(upcomingOrders);
  const [searchToken, setSearchToken] = useState<string>('');
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [searchState, setSearchState] = useState<string>(SearchState.BUY_IT_AGAIN); // 'initial', 'searching', 'results', 'noResults', 'error'
  const [productsAndOffers, setProductsAndOffers] = useState<ProductsAndOffers>({
    products: [],
    offers: [],
    source: '',
  });
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const [numFound, setNumFound] = useState<number>(0);
  const [nextPageToken, setNextPageToken] = useState<string>('');
  const [fulfillmentPreference, setFulfillmentPreference] = useState<any>(null);
  const [cameFromSearch, setCameFromSearch] = useState<boolean>(false);
  //const { setCameFromSearch } = useContext(BackButtonContext) as BackButtonContextType;

  const clearResults = () => {
    setSearchToken('');
    setSelectedIndex(-1);
    setProductsAndOffers({ products: [], offers: [], source: '' }); //TODO: Check if this is required
    setSuggestions([]);
    setSearchState(SearchState.BUY_IT_AGAIN);
  };

  const clearSuggestions = () => {
    setSuggestions([]);
  };

  const handleSearch = async (query) => {
    try {
      setSearchState(SearchState.SEARCHING);
      const searchSuggestions = await invoke(currentSessionContext, 'ensureActiveSession', () =>
        getAutoSuggestKeyWords(appData.environmentVariables, query),
      );

      const suggestions = get(searchSuggestions, 'data.response.suggestions', []);

      //If suggestions length > 10 , slice the suggestions to 10, otherwise keep the show the suggestions as is
      const limitedSuggestions = size(suggestions) > 10 ? slice(suggestions, 0, 10) : suggestions;

      setSuggestions(limitedSuggestions);
      localStorage.setItem('analyticsSearchTerm', query);
    } catch (error) {
      setSearchState(SearchState.SEARCH_TYPE_AHEAD_API_ERROR);
    }
  };
  const handleSelect = async (item) => {
    setSearchState(SearchState.SEARCHING);
    // Simulate API call for selected item
    try {
      //when user hit enter from search bar the item is a string and when user clicks on suggestion the item is an object
      const keyword = typeof item === 'string' ? item : item.dq || '';

      const response = await invoke(currentSessionContext, 'ensureActiveSession', () =>
        searchProducts(appData.environmentVariables, user, keyword, upcomingOrder),
      );

      const products = get(response, 'data.primaryProducts.response.docs', []);
      const nextPageToken = get(response, 'data.primaryProducts.response.miscInfo.nextPageToken', '');
      const offers = ENABLE_OTA_OFFERS ? get(response, 'data.offersData', []) : [];

      const bpns = JSON.parse(localStorage.getItem('snsNextOrderBpns') || '{}');
      const bpnKeys = keys(bpns);

      const foundCount = get(response, 'data.primaryProducts.response.numFound', 0);
      const filteredProducts = filter(products, (product) => !bpnKeys.includes(product.pid));

      setProductsAndOffers({ source: SourceResponse.search, products: filteredProducts, offers });
      setSearchToken(keyword); //Set searchToken in the state
      setCameFromSearch(true);
      setNumFound(foundCount); // Set numFound in the state
      setNextPageToken(nextPageToken); // Set nextPageToken in the state
      setFulfillmentPreference(get(upcomingOrder, 'fulfillment.fulfillmentType', '') === 'DUG' ? 'pickup' : 'delivery');
      setSearchState(products.length > 0 ? SearchState.PRODUCT_RESULTS : SearchState.NO_RESULTS);
      analyticsSearchEvent(response?.data?.primaryProducts?.response, keyword);
    } catch (error) {
      setSearchState(SearchState.SEARCH_PRODUCTS_API_ERROR);
      if (window['AB']?.DATALAYER) {
        window['AB'].DATALAYER.setError(
          'no-value',
          'ota-search-bar|error',
          'search-is-temporarily-unavailable-please-try-again-later',
        );
      }
    }
  };

  const analyticsSearchEvent = (response, keyword) => {
    try {
      if (window['AB']?.DATALAYER?.setOtaSnsSearch) {
        window['AB'].DATALAYER.setOtaSnsSearch(response, keyword);
      }
    } catch (error) {
      console.error('@AnalyticsTeam error:', error);
    }
  };

  return (
    <SearchContext.Provider
      value={{
        searchToken,
        setSearchToken,
        searchState,
        setSearchState,
        productsAndOffers,
        setProductsAndOffers,
        selectedIndex,
        suggestions,
        errorMessage,
        clearResults,
        handleSearch,
        clearSuggestions,
        handleSelect,
        numFound,
        nextPageToken,
        fulfillmentPreference,
        cameFromSearch,
        setCameFromSearch,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

interface SuggestionsListProps {
  searchState: string;
  suggestions: any[];
  searchToken: string;
  selectedIndex: number;
  handleSuggestionClick: (suggestion: string) => void;
}

const SuggestionsList = ({
  searchState,
  suggestions,
  searchToken,
  selectedIndex,
  handleSuggestionClick,
}: SuggestionsListProps) => (
  <>
    {size(suggestions) > 0 && searchToken && (
      <p className={suggestionListHeadingStyle} data-testid="suggestions-hint">
        Search for other items like "{searchToken}"
      </p>
    )}
    <ul className={suggestionListStyle} data-testid="suggestion-list">
      {map(suggestions, (suggestion, index) => {
        const querySuggestion = get(suggestion, 'dq');
        return (
          <li
            data-testid="suggestion-item"
            key={index}
            className={selectedIndex == index ? selectedSuggestionStyle : ''}
            onClick={() => handleSuggestionClick(suggestion)}
          >
            {querySuggestion}
          </li>
        );
      })}
    </ul>
  </>
);

interface SearchBarProps {
  searchBarRef: ForwardedRef<HTMLInputElement>;
  loadBuyItAgainProducts: () => Promise<void>;
  setProductQtyUpdateError: (error: boolean) => void;
}

export const SearchBar = ({ searchBarRef, loadBuyItAgainProducts, setProductQtyUpdateError }: SearchBarProps) => {
  const context = useContext(SearchContext) as SearchContextType;
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const {
    searchState,
    searchToken,
    setSearchToken,
    suggestions,
    clearResults,
    clearSuggestions,
    handleSearch,
    handleSelect,
  } = context;
  const localSearchBarRef = useRef<HTMLInputElement>(null);
  const inputRef = searchBarRef || localSearchBarRef;

  useEffect(() => {
    clearResults();
  }, []);

  useEffect(() => {
    setSelectedIndex(-1);
  }, [searchToken]);

  const handleChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const val = get(evt, 'target.value', '');
    setSearchToken(val);
    if (val.length >= SEARCH_AUTO_POPULATE_CHAR_COUNT) {
      handleSearch(val);
    } else if (val.length === 0) {
      clearResults();
    } else {
      clearSuggestions();
    }
  };

  const handleBackArrowClick = () => {
    // Clear the search token
    setSearchToken('');

    //Reset the focus state
    handleBlur();

    // Trigger loading the Buy IT Again products
    loadBuyItAgainProducts();

    // Reset product quantity update error state
    setProductQtyUpdateError(false);
  };

  const handleKey = (event) => {
    const key = get(event, 'key');
    const isEnterKey = key === 'Enter';
    const isArrowDownKey = key === 'ArrowDown';
    const isArrowUpKey = key === 'ArrowUp';

    if (isEnterKey) {
      if (selectedIndex >= 0) {
        const selectedSuggestion = get(suggestions[selectedIndex], 'dq');
        setSearchToken(selectedSuggestion);
        handleSelect(selectedSuggestion);
      } else {
        handleSelect(searchToken);
        setSearchToken(searchToken); //TODO: Verify this use case
      }
      clearSuggestions();
    } else if (isArrowDownKey && selectedIndex < suggestions.length - 1) {
      setSelectedIndex((prev) => prev + 1);
    } else if (isArrowUpKey && selectedIndex > 0) {
      setSelectedIndex((prev) => prev - 1);
    }
    if (isEnterKey || isArrowDownKey || isArrowUpKey) {
      event.preventDefault();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    const selectedSuggestion = get(suggestion, 'dq');
    setSearchToken(selectedSuggestion);
    handleSelect(selectedSuggestion);
    clearSuggestions();
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  return (
    <div className={searchBarContainer} data-testid="search-bar">
      <div className={`${searchBarInnerContainer} ${isFocused ? focused : ''}`}>
        {searchToken ? <BackArrowIcon onClick={handleBackArrowClick} /> : <SearchIcon />}
        <input
          ref={inputRef}
          className={`${searchBarStyle} ${selectedIndex >= 0 ? searchBarCaretStyle : ' '}`}
          type="text"
          placeholder="Search products"
          onChange={handleChange}
          onKeyDown={handleKey}
          onClick={() => setSelectedIndex(-1)}
          value={searchToken}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        {searchToken && <CloseIcon onClick={clearResults} />}
      </div>
      {searchState === SearchState.SEARCHING && (
        <SuggestionsList
          suggestions={suggestions}
          searchToken={searchToken}
          selectedIndex={selectedIndex}
          handleSuggestionClick={handleSuggestionClick}
          searchState={searchState}
        />
      )}
    </div>
  );
};

interface ProductGridProps {
  products?: any; //TODO fix the type
  offers?: object;
  componentType?: string;
  SNS_PROGRAM_TYPE?: string;
  singleOffer?: object;
  offerType?: string;
  product?: object;
  orderData?: object;
  numFound?: number;
  nextPageToken?: string;
  searchToken?: string;
  userFulfillmentPreference?: string;
}

export const ProductGrid = (props: ProductGridProps) => {
  const { appData } = useApp();

  const products = get(props, 'products', []);
  const offers = get(props, 'offers', []);
  const componentType = get(props, 'componentType', '');
  const SNS_PROGRAM_TYPE = get(props, 'SNS_PROGRAM_TYPE', '1P_SNS');
  const B4U_APPNAME = 'b4u';
  const offerType = get(props, 'offerType', '');
  const numFound = get(props, 'numFound', 0);
  const nextPageToken = get(props, 'nextPageToken', '');
  const searchToken = get(props, 'searchToken', '');
  const singleOffer = get(props, 'singleOffer', {});
  const multipleCouponsProduct = get(props, 'product', {});
  const userFulfillmentPreference = get(props, 'userFulfillmentPreference', '');
  const productsObject = nextPageToken ? { products, nextPageToken } : { products };

  const [v2ThemeEnabled, setV2ThemeEnabled] = useState<any>(null);

  useEffect(() => {
    const pageElement = document.querySelector('[data-enablev2theme]');

    if (pageElement) {
      const v2ThemeEnabled = pageElement.getAttribute('data-enablev2theme');
      setV2ThemeEnabled(Boolean(v2ThemeEnabled));
    }
  }, [v2ThemeEnabled]);

  useEffect(() => {
    if (componentType === 'singleCouponDetail') {
      window['appConfigServiceRef'].service.postUserInfoSource.next('initFunc');
      window['appConfigServiceRef'].service._postUserInfoSource.next('initFunc');
    }
  }, [componentType]);

  const configObj = {
    programType: SNS_PROGRAM_TYPE,
    'data-search-key': replace(searchToken, /'/g, ''),
    'data-products-found': numFound,
    'data-fulfillment-channel': userFulfillmentPreference,
  };

  const renderComponent = () => {
    switch (componentType) {
      case 'productGrid':
        return `
        <div class=${productGridStyle}>
          <product-generic-grid-al-v1
            data-products='${replace(JSON.stringify(productsObject), /'/g, '')}'
            data-offers='${replace(JSON.stringify({ offers }), /'/g, '')}'
            data-configObj='${JSON.stringify(configObj)}'
            data-infinite-scroll-type="client"
            data-load-type="loadmore"
            data-load-more-mobile-items="30"
            data-load-more-tablet-items="30"
            data-load-more-desktop-items="30"
          ></product-generic-grid-al-v1>
        </div>
        `;
      case 'singleCouponDetail':
        return `
          <div class="main-wrapper www-wrapper nextgen-main-wrapper" data-enablev2theme="true">
           <offer-detail-al-v1
                  data-offer='${replace(JSON.stringify(singleOffer), /'/g, '')}'
                  data-configObj='${JSON.stringify({ programType: SNS_PROGRAM_TYPE, appName: B4U_APPNAME })}'>
                </offer-detail-al-v1>
          </div>
        `;
      case 'multipleCoupons':
        return `
          <div class="sidebar-right-drawer">
            <coupon-list-al-v1
               data-product='${replace(JSON.stringify(multipleCouponsProduct), /'/g, '')}'
               data-configObj='${JSON.stringify({ programType: SNS_PROGRAM_TYPE })}'>
            </coupon-list-al-v1>
          </div>
        `;
      default:
        return '';
    }
  };

  return <div dangerouslySetInnerHTML={{ __html: renderComponent() }} />;
};

const AddOneTimeModalContent = (props) => {
  const searchBarRef = useRef<HTMLInputElement>(null);
  const context = useContext(SearchContext) as SearchContextType;
  const currentSessionContext = useContext(SessionContext) as SessionContextType;

  const { setBackButtonState, setMultipleCouponsData, setShowBackButton, setCameFromMultipleCouponsScreen } =
    useContext(BackButtonContext) as BackButtonContextType;
  const { appData } = useApp();
  const user = get(currentSessionContext, 'user');

  const titleTextCenter = get(props, 'titleTextCenter');
  const upcomingOrders = get(props, 'upcomingOrders');
  const showOneTimeModal = get(props, 'showOneTimeModal');
  const setShowOneTimeModal = get(props, 'setShowOneTimeModal');
  const showBottomTransitionModal = get(props, 'showBottomTransitionModal');
  const setShowBottomTransitionModal = get(props, 'setShowBottomTransitionModal');
  const componentType = get(props, 'componentType', 'productGrid');
  const onReviewOrderButton = get(props, 'onReviewOrder');

  const [itemsAdded, setItemsAdded] = useState<number>(0);
  const [productQtyUpdated, setProductQtyUpdated] = useState<boolean>(false);

  //State to store updated OTA BPNS
  const [otaAddedBpns, setOtaAddedBpns] = useState<any>({});

  const [orderData, setOrderData] = useState<any>(upcomingOrders);

  const {
    searchState,
    productsAndOffers,
    errorMessage,
    setProductsAndOffers,
    setSearchState,
    searchToken,
    numFound,
    nextPageToken,
    fulfillmentPreference,
    setSearchToken,
  } = context;

  const [eventData, setEventData] = useState<any>({});
  const [modalTitle, setModalTitle] = useState<string>(
    formatString(AppLabels.PendingOrderContent.oneTimeAddsModalTitle, {
      upcomingOrderDate: moment(get(orderData, 'upcomingOrder.orderDate'), 'YYYY-MM-DD').format('ddd, MMM D'),
    }),
  );
  const [productQtyUpdateError, setProductQtyUpdateError] = useState<boolean>(false);

  useEffect(() => {
    if (upcomingOrders) {
      setOrderData(upcomingOrders);
    }
  }, [upcomingOrders]);

  useEffect(() => {
    if (showOneTimeModal) {
      //Extracting  productBpn's from the upcoming order and save to localStorage
      if (orderData) {
        //remove the bpns from local storage to maintain the latest bpns
        localStorage.removeItem('snsNextOrderBpns');
        const orderItemsBpns = map(orderData.upcomingOrder.orderItems, (item) => ({
          productBpn: item.productBpn,
          qty: item.qty || 1,
        }));
        const oneTimeOrderItemsBpns = map(orderData.upcomingOrder.oneTimeOrderItems, (item) => ({
          productBpn: item.productBpn,
          qty: item.qty || 1,
        }));
        const allBpns = [...orderItemsBpns, ...oneTimeOrderItemsBpns].reduce((acc, item) => {
          acc[item.productBpn] = item.qty;
          return acc;
        }, {});
        // Save the bpns to local storage
        localStorage.setItem('snsNextOrderBpns', JSON.stringify(allBpns));
      }

      if (componentType === 'productGrid') {
        //for displaying pdp grid in local
        if (window.location.href.indexOf('local') > 0) {
          if (window['AB'] && window['AB'].userInfo) {
            window['AB'].userInfo.userType = 'R';
            window['AB'].userInfo.preferenceMode = 'delivery';
          }
        }

        loadBuyItAgainProducts();
      }
    }
  }, [showOneTimeModal, componentType]);

  useEffect(() => {
    const handleEvent = (event: CustomEvent) => {
      setProductQtyUpdateError(false);
      const eventType = get(event.detail, 'type');
      if (eventType === 'singleCoupon') {
        const singleOffer = get(event.detail, 'coupons');
        setEventData({ singleOffer });
        setBackButtonState(SearchState.SINGLE_COUPON_DETAIL, { product: {} });
        setSearchState(SearchState.SINGLE_COUPON_DETAIL);
      } else if (eventType === 'multipleCoupons') {
        const multipleProduct = get(event.detail, 'product');
        setEventData({ multipleProduct });
        setMultipleCouponsData({ multipleProduct });
        setBackButtonState(SearchState.MULTIPLE_COUPONS, { product: multipleProduct });
        setSearchState(SearchState.MULTIPLE_COUPONS);
        setCameFromMultipleCouponsScreen(true);
      } else if (eventType === 'productQtyUpdateSuccess') {
        const bpn = get(event.detail, 'data-bpn');
        const qty = parseInt(get(event.detail, 'qty', '0'), 10);

        // Update both snsNextOrderBpns and snsNextOrderOtaAddedBpns
        const storedBpns = JSON.parse(localStorage.getItem('snsNextOrderBpns') || '{}');
        const updatedBpns = cloneDeep(storedBpns);

        //Update otAddedBpns state based on event
        const updatedOtaBpns = cloneDeep(otaAddedBpns);

        if (qty === 0) {
          const removeOtaBpn = updatedOtaBpns[bpn] || 0;
          delete updatedBpns[bpn]; // Remove BPN if data-bpn is '0'
          delete updatedOtaBpns[bpn]; // Remove BPN if data-bpn is '0'
          setItemsAdded((prev) => prev - removeOtaBpn);
        } else {
          const previousQty = updatedBpns[bpn] || 0;
          updatedBpns[bpn] = qty; // Replace with the new quantity as per the event
          updatedOtaBpns[bpn] = qty; // Replace with the new quantity as per the event
          setItemsAdded((prev) => prev - previousQty + qty);
        }

        localStorage.setItem('snsNextOrderBpns', JSON.stringify(updatedBpns));

        //Update the otaAddedBpns state
        setOtaAddedBpns(updatedOtaBpns);
        setProductQtyUpdated(true);
        const InfiniteLoadingIssue = document.getElementById('sns-ota-test-id') as HTMLElement;
        if (InfiniteLoadingIssue) {
          InfiniteLoadingIssue.click();
        }
      } else if (eventType === 'productQtyUpdateError') {
        setProductQtyUpdateError(true);
        try {
          if (window['AB']?.DATALAYER && localStorage.getItem('analyticsProdAction')) {
            window['AB'].DATALAYER.setError(
              'no-value',
              `ota|${localStorage.getItem('analyticsProdAction')}-error`,
              AppLabels.PendingOrderContent.oneTimeAddModalErrorBannarText
                .toLowerCase()
                .replace(/\./g, '')
                .replace(/\s+/g, '-'),
            );
          }
        } catch (error) {
          console.error('@AnalyticsTeam error:', error);
        }
      } else if (eventType === 'loadBuyItAgainProducts') {
        setShowBackButton(false);
        setSearchState(SearchState.PRODUCT_RESULTS);
        loadBuyItAgainProducts();
      }
    };

    window.addEventListener('snsOneTime', handleEvent as EventListener);

    return () => {
      window.removeEventListener('snsOneTime', handleEvent as EventListener);
    };
  }, [otaAddedBpns, setMultipleCouponsData, setBackButtonState]);

  useEffect(() => {
    // Reset productQtyUpdateError state on modal searchState change
    setProductQtyUpdateError(false);

    if (searchState === SearchState.SINGLE_COUPON_DETAIL) {
      setModalTitle('Offer Details');
    } else if (searchState === SearchState.MULTIPLE_COUPONS) {
      setModalTitle('Available Offers');
    } else {
      setModalTitle(
        formatString(AppLabels.PendingOrderContent.oneTimeAddsModalTitle, {
          yourText: isMobileScreen() ? '' : 'your',
          upcomingOrderDate: moment(get(orderData, 'upcomingOrder.orderDate'), 'YYYY-MM-DD').format('ddd, MMM D'),
        }),
      );
    }
  }, [searchState, orderData]);

  const loadBuyItAgainProducts = async (): Promise<void> => {
    try {
      const response = await invoke(currentSessionContext, 'ensureActiveSession', () =>
        buyItAgainService(appData.environmentVariables, user, orderData),
      );
      const products = get(response, 'data.productsinfo', []);
      const offers = ENABLE_OTA_OFFERS ? get(response, 'data.offersData', []) : [];

      // Retrieve BPNS from local storage
      const bpns = JSON.parse(localStorage.getItem('snsNextOrderBpns') || '{}');

      const bpnKeys = keys(bpns);

      // Filter out the products that are already in the upcoming order
      const filteredProducts = filter(products, (product) => !bpnKeys.includes(product.pid));

      setProductsAndOffers({ source: SourceResponse.buyItAgain, products: filteredProducts, offers });
      setSearchState(SearchState.PRODUCT_RESULTS);
      analyticsPageLoad(get(filteredProducts, 'length', 0) > 0 ? 'bia' : 'bia-no-results');
    } catch (error) {
      setSearchState(SearchState.BUY_IT_AGAIN_API_ERROR);
      if (searchBarRef.current) {
        searchBarRef.current.focus();
      }
    }
  };

  const renderBuyItAgainHeader = () => {
    const source = get(productsAndOffers, 'source', '');
    const products = get(productsAndOffers, 'products.length', 0);

    if (source === SourceResponse.buyItAgain && products > 0 && searchState === SearchState.PRODUCT_RESULTS) {
      return <h1 className={buyItAgainTitleStyle}>Buy it again</h1>;
    }
    return null;
  };

  const handleReviewOrderClick = () => {
    setShowOneTimeModal(false);
    setShowBottomTransitionModal(false);
    setOtaAddedBpns({}); // Reset otaAddedBpns state on modal close
    setItemsAdded(0); // Reset itemsAdded state on modal close
    setProductQtyUpdated(false); // Reset productQtyUpdated state on modal close
    setSearchToken(''); // Reset searchToken state on modal close
    setShowBackButton(false); // Reset showBackButton state on modal close
    setEventData({}); // Reset eventData state on modal close
    setCameFromMultipleCouponsScreen(false);
    localStorage.removeItem('snsNextOrderBpns'); // Remove the bpns from local storage on modal close
    if (onReviewOrderButton) {
      onReviewOrderButton();
    }
  };

  const handleCloseModal = () => {
    if (itemsAdded > 0) {
      handleReviewOrderClick();
    } else {
      setShowOneTimeModal(false);
      setShowBottomTransitionModal(false);
      setOtaAddedBpns({}); // Reset otaAddedBpns state on modal close
      setItemsAdded(0); // Reset itemsAdded state on modal close
      setProductQtyUpdated(false); // Reset productQtyUpdated state on modal close
      setSearchToken(''); // Reset searchToken state on modal close
      setShowBackButton(false);
      setEventData({}); // Reset eventData state on modal close
      setCameFromMultipleCouponsScreen(false);
      localStorage.removeItem('snsNextOrderBpns'); // Remove the bpns from local storage on modal close
    }
  };

  const analyticsPageLoad = (page: string) => {
    try {
      if (window['AB']?.DATALAYER) {
        window['AB'].DATALAYER.SNSInit(page, upcomingOrders);
      }
    } catch (error) {
      // Handle any errors that occur during execution
      console.error('@AnalyticsTeam error:', error);
    }
  };

  const renderContent = cond([
    [matches(SearchState.BUY_IT_AGAIN), () => <div></div>],
    [matches(SearchState.SEARCHING), () => <div></div>],
    [
      matches(SearchState.PRODUCT_RESULTS),
      () => (
        <ProductGrid
          products={productsAndOffers.products}
          offers={productsAndOffers.offers}
          SNS_PROGRAM_TYPE={SNS_PROGRAM_TYPE}
          componentType={ComponentType.PRODUCT_GRID}
          orderData={orderData}
          numFound={numFound}
          nextPageToken={nextPageToken}
          userFulfillmentPreference={fulfillmentPreference}
          searchToken={searchToken}
        />
      ),
    ],
    [
      matches(SearchState.SINGLE_COUPON_DETAIL),
      () => (
        <ProductGrid
          componentType={ComponentType.OFFER_DETAIL}
          SNS_PROGRAM_TYPE={SNS_PROGRAM_TYPE}
          singleOffer={eventData.singleOffer}
          orderData={orderData}
        />
      ),
    ],
    [
      matches(SearchState.MULTIPLE_COUPONS),
      () => (
        <ProductGrid
          componentType={ComponentType.MULTIPLE_COUPONS}
          SNS_PROGRAM_TYPE={SNS_PROGRAM_TYPE}
          product={eventData.multipleProduct}
          orderData={orderData}
        />
      ),
    ],
    [
      matches(SearchState.NO_RESULTS),
      () => (
        <div className={suggestionListHeadingStyle}>Sorry, we couldn't find any products matching "{searchToken}"</div>
      ),
    ],
    [matches(SearchState.ERROR), () => <div>{errorMessage}</div>],
    [matches(SearchState.SEARCH_TYPE_AHEAD_API_ERROR), () => <div></div>],
    [matches(SearchState.BUY_IT_AGAIN_API_ERROR), () => <div></div>],
    [
      matches(SearchState.SEARCH_PRODUCTS_API_ERROR),
      () => <div>Search is temporarily unavailable. Please try again later.</div>,
    ],
  ]);

  return (
    <Modal
      titleTextCenter={titleTextCenter}
      isOneTimeAddsModal={true}
      className={flexRow}
      titleText={modalTitle}
      onClose={() => {
        handleCloseModal();
      }}
      fillScreen={true}
      overFlowBodyHidden={true}
      show={showOneTimeModal}
      showBottomTransitionModal={showBottomTransitionModal}
      modalId={otaModalConfigId}
    >
      <div className={contentWrapperStyle}>
        {componentType === 'productGrid' &&
          searchState !== SearchState.SINGLE_COUPON_DETAIL &&
          searchState !== SearchState.MULTIPLE_COUPONS && (
            <SearchBar
              searchBarRef={searchBarRef}
              loadBuyItAgainProducts={loadBuyItAgainProducts}
              setProductQtyUpdateError={setProductQtyUpdateError}
            />
          )}
        {productQtyUpdateError && (
          <div className={productGridErrorBannerStyle}>
            <ErrorIcon />{' '}
            <div className={productGridErrorTextStyle}>
              {AppLabels.PendingOrderContent.oneTimeAddModalErrorBannarText}
            </div>
          </div>
        )}
        {renderBuyItAgainHeader()}
        <div id="sns-ota-test-id"></div>
        {searchState !== SearchState.SEARCHING && (
          <div className={oneTimeAddModalContentStyle}>{renderContent(searchState)}</div>
        )}
      </div>
      {productQtyUpdated && searchState === SearchState.PRODUCT_RESULTS && itemsAdded > 0 && (
        <div className={submitButtonContainerStyle}>
          <div className={oneTimeAddonsSummaryStyle}>
            <GreenRoundedIcon />
            {` ${itemsAdded} item${itemsAdded > 1 ? 's' : ''} added`}
          </div>
          <input
            className={submitButtonStyle}
            type="submit"
            value="Review order"
            onClick={() => {
              handleReviewOrderClick();
            }}
          />
        </div>
      )}
    </Modal>
  );
};

const AddOneTimeModal = ({
  titleTextCenter,
  upcomingOrders,
  showOneTimeModal,
  setShowOneTimeModal,
  showBottomTransitionModal,
  setShowBottomTransitionModal,
  componentType,
  onReviewOrder,
}) => (
  <SearchProvider upcomingOrders={upcomingOrders}>
    <BackButtonProvider>
      <AddOneTimeModalContent
        titleTextCenter={titleTextCenter}
        upcomingOrders={upcomingOrders}
        showOneTimeModal={showOneTimeModal}
        setShowOneTimeModal={setShowOneTimeModal}
        showBottomTransitionModal={showBottomTransitionModal}
        setShowBottomTransitionModal={setShowBottomTransitionModal}
        componentType={componentType}
        onReviewOrder={onReviewOrder}
      />
    </BackButtonProvider>
  </SearchProvider>
);

export const AddOneTimeItems = (props) => {
  const [showOneTimeModal, setShowOneTimeModal] = useState(false);
  const [showBottomTransitionModal, setShowBottonTransitionModal] = useState(false);
  const expanded = get(props, 'expanded');
  const upcomingOrders = get(props, 'upcomingOrders');
  const refreshNextOrder = get(props, 'refreshNextOrder');

  const componentType = get(props, 'componentType', 'productGrid');

  const handleReviewOrderClick = () => {
    // Trigger the refreshNextOrder method when review order button clicked
    if (refreshNextOrder) {
      refreshNextOrder();
    }
  };

  const analyticsAddToOrder = () => {
    try {
      if (window['AB']?.DATALAYER) {
        window['AB'].DATALAYER.analyticsCTATracking(
          'cta:customer-account:manage-schedule-and-save:upcoming-orders-enrolled|button|add-items-to-this-order',
        );
      }
    } catch (error) {
      // Handle any errors that occur during execution
      console.error('@AnalyticsTeam error:', error);
    }
  };

  return (
    <div
      className={`${expanded ? addOneTimeSectionStyleExpanded : addOneTimeSectionStyleCollapsed} ${dateSectionExpandedStyle}`}
    >
      <div className={addOneTime}>
        <div className={addOneTimeDetailsStyle}>{AppLabels.PendingOrderContent.AddOneTimeItemInfo}</div>
        <button
          onClick={() => {
            setShowOneTimeModal(true);
            setShowBottonTransitionModal(true);
            analyticsAddToOrder();
          }}
          data-testid="add-one-time-item-button"
          className={addOneTimeItemButtonStyle}
          aria-label={'Add items to this order'}
          aria-labelledby={'Add items to this order for upcoming order'}
        >
          Add items to this order
        </button>
      </div>
      <AddOneTimeModal
        titleTextCenter={true}
        upcomingOrders={upcomingOrders}
        showOneTimeModal={showOneTimeModal}
        setShowOneTimeModal={setShowOneTimeModal}
        showBottomTransitionModal={showBottomTransitionModal}
        setShowBottomTransitionModal={setShowBottonTransitionModal}
        componentType={componentType}
        onReviewOrder={handleReviewOrderClick}
      />
    </div>
  );
};

AddOneTimeItems.propTypes = {
  expanded: PropTypes.bool,
  upcomingOrders: PropTypes.object.isRequired,
  refreshNextOrder: PropTypes.func,
};
